import { formatPhp, formatDate, formatRatePct, buildAmortizationSchedule } from "@/lib/calc";

// Page 3 of the print: declining-balance amortization schedule.
// Mirrors the "Amortization_JL Final" Excel exactly (see buildAmortizationSchedule).
export default function AmortizationPage({
  name,
  address,
  loanAmount,
  termMonths,
  annualRate,
  dateGranted,
  showLogo = true,
}) {
  const { payment, monthlyRate, rows } = buildAmortizationSchedule({
    annualRate,
    termMonths,
    loanAmount,
    dateGranted,
  });

  const totalInterest = rows.reduce((s, r) => s + r.intBal, 0);
  const totalPrincipal = rows.reduce((s, r) => s + r.principal, 0);
  const totalPayments = rows.reduce((s, r) => s + r.amort, 0);

  return (
    <div
      className="amort-page disclosure-doc text-[13px] leading-relaxed text-slate-900"
      data-testid="amortization-page"
    >
      {/* Header */}
      <div className="text-center border-b-2 border-slate-900 pb-4 mb-5 break-inside-avoid">
        {showLogo && (
          <img src="/logo.png" alt="Logo" className="print-logo h-20 w-auto object-contain mx-auto mb-2" />
        )}
        <h1 className="text-lg font-bold tracking-wide">CEBUANA LHUILLIER RURAL BANK</h1>
        <h2 className="text-sm font-bold uppercase mt-1">Schedule of Amortization</h2>
      </div>

      {/* Loan summary */}
      <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-5">
        <div>
          <span className="font-semibold">Name: </span>
          <span data-testid="amort-name">{name || "\u00A0"}</span>
        </div>
        <div>
          <span className="font-semibold">Interest Rate (p.a.): </span>
          <span data-testid="amort-rate">{formatRatePct(annualRate)}%</span>
        </div>
        <div>
          <span className="font-semibold">Address: </span>
          <span data-testid="amort-address">{address || "\u00A0"}</span>
        </div>
        <div>
          <span className="font-semibold">Loan Term: </span>
          <span data-testid="amort-term">{termMonths} months</span>
        </div>
        <div>
          <span className="font-semibold">Loan Amount: </span>
          <span className="tabular-nums" data-testid="amort-loan-amount">{formatPhp(loanAmount)}</span>
        </div>
        <div>
          <span className="font-semibold">Date Granted: </span>
          <span data-testid="amort-date-granted">{formatDate(dateGranted)}</span>
        </div>
      </div>

      {/* Schedule table (columns match the Excel: Due Date | Beginning Balance |
          Principal | Int Bal | Amort./Month) */}
      <table className="w-full border border-slate-900 border-collapse text-[12px] mb-5 tabular-nums">
        <thead>
          <tr className="bg-slate-100">
            <th className="border border-slate-900 p-2 text-left">Due Date</th>
            <th className="border border-slate-900 p-2 text-right">Beginning Balance</th>
            <th className="border border-slate-900 p-2 text-right">Principal</th>
            <th className="border border-slate-900 p-2 text-right">Int Bal</th>
            <th className="border border-slate-900 p-2 text-right">Amort./Month</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.month} data-testid={`amort-row-${r.month}`}>
              <td className="border border-slate-900 p-2 text-left">{formatDate(r.dueDate)}</td>
              <td className="border border-slate-900 p-2 text-right">{formatPhp(r.beginningBalance)}</td>
              <td className="border border-slate-900 p-2 text-right">{formatPhp(r.principal)}</td>
              <td className="border border-slate-900 p-2 text-right">{formatPhp(r.intBal)}</td>
              <td className="border border-slate-900 p-2 text-right">{formatPhp(r.amort)}</td>
            </tr>
          ))}
          <tr className="bg-slate-100 font-semibold">
            <td className="border border-slate-900 p-2 text-left">TOTAL</td>
            <td className="border border-slate-900 p-2 text-right">&nbsp;</td>
            <td className="border border-slate-900 p-2 text-right" data-testid="amort-total-principal">{formatPhp(totalPrincipal)}</td>
            <td className="border border-slate-900 p-2 text-right" data-testid="amort-total-interest">{formatPhp(totalInterest)}</td>
            <td className="border border-slate-900 p-2 text-right" data-testid="amort-total-payments">{formatPhp(totalPayments)}</td>
          </tr>
        </tbody>
      </table>

      <p className="text-[11px] text-slate-500 mb-6">
        Amortization computed on a diminishing-balance basis at {formatRatePct(monthlyRate)}% per month
        ({formatRatePct(annualRate)}% per annum). Fixed monthly amortization: {formatPhp(payment)}.
      </p>

      {/* Conforme */}
      <div className="mt-10 break-inside-avoid">
        <p className="font-semibold mb-8">Conforme:</p>
        <div className="w-2/3">
          <div className="border-t border-slate-900 pt-1 min-h-[1.5rem]">
            <span className="text-base text-slate-900" data-testid="amort-conforme-name">{name || "\u00A0"}</span>
          </div>
          <p className="text-[11px] font-semibold text-slate-700 leading-tight mt-1">
            (Signature over printed name)
          </p>
        </div>
      </div>
    </div>
  );
}
